import { planIncludesTeamCollaboration, type BackendPlan } from '../lib/stripe-config'
import type { ActiveSubscription, UsageData } from '../data/accountTypes'
import type { DailyCreditUsage } from '../schemas/creditUsage'
import { MOCK_AUTH_USER } from './mockAuth'
import { MOCK_TEAM_IDS, MOCK_TEAM_PLAN_KEYS, mockFetchTeams } from './mockTeams'

export const MOCK_WORKSPACE_ID = 'mock-workspace-001'

/** Plan catalog mirrored from production tiers shown in the subscription picker. */
export function mockFetchStripePlans(): { plans: BackendPlan[] } {
  return {
    plans: [
      {
        key: 'free-v1',
        name: 'Free',
        displayName: 'Free',
        tier: 'free',
        interval: 'month',
        visibility: 'public',
        monthlyPriceCents: 0,
        billingId: 'price_mock_free',
      },
      {
        key: 'free-v1-yearly',
        name: 'Free',
        displayName: 'Free',
        tier: 'free',
        interval: 'year',
        visibility: 'public',
        monthlyPriceCents: 0,
        billingId: 'price_mock_free_yearly',
      },
      {
        key: 'individual-v1',
        name: 'Individual',
        displayName: 'Individual',
        tier: 'individual',
        interval: 'month',
        visibility: 'public',
        monthlyPriceCents: 2000,
        billingId: 'price_mock_individual',
      },
      {
        key: 'individual-v1-yearly',
        name: 'Individual',
        displayName: 'Individual',
        tier: 'individual',
        interval: 'year',
        visibility: 'public',
        monthlyPriceCents: 1700,
        billingId: 'price_mock_individual_yearly',
      },
      {
        key: 'team-v1',
        name: 'Team',
        displayName: 'Team',
        tier: 'team',
        interval: 'month',
        visibility: 'public',
        monthlyPriceCents: 20000,
        billingId: 'price_mock_team',
      },
      {
        key: 'team-v1-yearly',
        name: 'Team',
        displayName: 'Team',
        tier: 'team',
        interval: 'year',
        visibility: 'public',
        monthlyPriceCents: 17000,
        billingId: 'price_mock_team_yearly',
      },
    ],
  }
}

/** Active subscription for the mock user — Free tier by default. */
export function mockFetchSubscription(): {
  subscription: ActiveSubscription
  workspaceId: string
} {
  return {
    subscription: {
      plan: { key: 'free-v1', name: 'Free' },
      status: 'active',
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
      seatCount: 1,
    },
    workspaceId: MOCK_WORKSPACE_ID,
  }
}

function daysAgoDate(n: number): string {
  const d = new Date('2026-04-15T12:00:00.000Z')
  d.setDate(d.getDate() - n)
  // Vary the time-of-day a little so distinct entries show distinct timestamps.
  d.setHours(d.getHours() - n, (n * 7) % 60, 0, 0)
  return d.toISOString()
}

/**
 * The per-day series behind Billing's usage graph and the credit meter's burn rate.
 *
 * Built from the same per-task dates as mockFetchUsage rather than from invented numbers, so the
 * graph, the balance, and the meter's "at this rate" estimate all describe one fixture. Without this
 * the two surfaces that read /api/usage-daily are unusable in mock mode.
 */
export function mockFetchDailyCreditUsage(input: {
  start_date: string
  end_date: string
  team_id?: string | null
}): DailyCreditUsage {
  const usage = mockFetchUsage(input.team_id ?? null)
  const day = (iso: string) => iso.slice(0, 10)
  const tasks = usage.projects.flatMap((project) =>
    (project.tasks ?? []).map((task) => ({ ...task, refunded: project.qa_costs_refunded === true })),
  )

  // The fixture's task dates hang off a fixed anchor (see daysAgoDate), so their literal days fall
  // outside any window a live client asks for. Each task keeps its distance from the most recent one
  // and the set slides forward to end today — which preserves the shape of the fixture (clusters, quiet
  // days, the refunded project) while landing it in the range that was requested.
  const dayMs = 86_400_000
  const anchor = tasks.reduce((latest, task) => Math.max(latest, Date.parse(task.date)), 0)
  const endsAt = Date.parse(`${input.end_date}T00:00:00Z`)

  const gross = new Map<string, number>()
  const refunded = new Map<string, number>()
  for (const task of tasks) {
    const offsetDays = Math.round((anchor - Date.parse(task.date)) / dayMs)
    const key = day(new Date(endsAt - offsetDays * dayMs).toISOString())
    if (task.refunded) refunded.set(key, (refunded.get(key) ?? 0) + task.credits)
    gross.set(key, (gross.get(key) ?? 0) + task.credits)
  }

  const days: DailyCreditUsage['days'] = []
  for (const cursor = new Date(`${input.start_date}T00:00:00Z`); day(cursor.toISOString()) <= input.end_date; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const key = day(cursor.toISOString())
    const grossCredits = gross.get(key) ?? 0
    const refundedCredits = refunded.get(key) ?? 0
    days.push({
      day: key,
      gross_credits: grossCredits,
      refunded_credits: refundedCredits,
      net_credits: grossCredits - refundedCredits,
      granted_credits: 0,
    })
  }

  return {
    start_date: input.start_date,
    end_date: input.end_date,
    timezone: 'UTC',
    as_of: new Date().toISOString(),
    available_credits: usage.available_credits,
    balance_status: 'resolved',
    usage_waived: usage.usage_waived ?? false,
    days,
  }
}

/** Credit usage tied to mock pipeline projects (Checkout demo + Marketing blog).
 *
 * `scope` mirrors the real endpoint's parameters: a team id reports that team's balance, and 'own'
 * pins the answer to the mock user's own team the way Settings › Billing asks for it. With neither,
 * the best-funded team wins, as pickPrimaryTeam does in production.
 *
 * The daily series behind /api/usage-daily is derived from the same task dates (see
 * mockFetchDailyCreditUsage), so a balance and the graph explaining it can never disagree here. */
/**
 * Dev-only balance override, read from the URL: `?mockCredits=<left>/<grant>/<planKey>`, plus
 * `&mockOwner=0` for a project funded by someone else's plan and `&mockWaived=1` for an active usage
 * code. The fixture has one healthy free account and no way to reach the states that matter most — low,
 * out, and a teammate's view — and those are exactly the states worth looking at while building.
 */
function creditOverride(): { remaining: number; grant: number; planKey: string; isOwner: boolean; waived: boolean } | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const spec = params.get('mockCredits')
  if (!spec) return null
  const [left, grant, planKey] = spec.split('/')
  if (left === undefined || grant === undefined) return null
  return {
    remaining: Number(left),
    grant: Number(grant),
    planKey: planKey || 'free-v1',
    isOwner: params.get('mockOwner') !== '0',
    waived: params.get('mockWaived') === '1',
  }
}

export function mockFetchUsage(scope?: string | null): UsageData {
  const checkoutTasks = [
    {
      task_id: 'mock-usage-exp-001',
      kind: 'exploration' as const,
      label: 'Explore checkout tax edge cases',
      date: daysAgoDate(2),
      credits: 1,
    },
    {
      task_id: 'mock-usage-journey-001',
      kind: 'journey' as const,
      label: 'Happy path checkout',
      date: daysAgoDate(3),
      credits: 0.5,
    },
    {
      task_id: 'mock-usage-followup-001',
      kind: 'task' as const,
      label: 'Review payment modal accessibility',
      date: daysAgoDate(5),
      credits: 1,
    },
    {
      task_id: 'mock-usage-exp-002',
      kind: 'exploration' as const,
      label: 'Explore coupon validation flows',
      date: daysAgoDate(8),
      credits: 1,
    },
    {
      task_id: 'mock-usage-journey-002',
      kind: 'journey' as const,
      label: 'Guest checkout journey',
      date: daysAgoDate(10),
      credits: 0.5,
    },
  ]

  const blogTasks = [
    {
      task_id: 'mock-usage-exp-003',
      kind: 'exploration' as const,
      label: 'Explore newsletter signup flow',
      date: daysAgoDate(4),
      credits: 1,
    },
    {
      task_id: 'mock-usage-journey-003',
      kind: 'journey' as const,
      label: 'Load the main page',
      date: daysAgoDate(7),
      credits: 0.5,
    },
  ]

  const checkoutTotal = checkoutTasks.reduce((sum, t) => sum + t.credits, 0)
  const blogTotal = blogTasks.reduce((sum, t) => sum + t.credits, 0)
  // The blog project's costs were refunded (see below), so they net to zero used credits.
  const ownTotalUsed = checkoutTotal
  // The real Free grant (PLAN_TIER_CREDIT_GRANTS in netlify/functions/lib/credits.ts), so anything that
  // states the number in dev — the credit meter, the welcome dialog — states the production one.
  const freeGrant = 25

  const ownProjects = [
    {
      project_id: 'mock-proj-checkout',
      project_name: 'Checkout demo',
      total_credits: checkoutTotal,
      tasks: checkoutTasks,
    },
    {
      // QA costs refunded (e.g. an accepted missing-bug report) — net usage is 0 and the refunded
      // badge shows. The badge keys off qa_costs_refunded (project-level free QA), not the refunded
      // credit total. Demonstrates the refunded-project display in dev mock.
      project_id: 'mock-proj-blog',
      project_name: 'Marketing blog',
      total_credits: 0,
      refunded_credits: blogTotal,
      qa_costs_refunded: true,
      tasks: blogTasks,
    },
  ]

  // Teams the viewer does not own, funded by their owners' plans and spent by people who aren't the
  // viewer. This is the case a personal account can never produce, and the one worth being able to see
  // in mock mode: a member or admin has to be able to read the balance their own runs spend.
  const acmeTasks = [
    {
      task_id: 'mock-usage-acme-exp-001',
      kind: 'exploration' as const,
      label: 'Explore the admin console',
      date: daysAgoDate(1),
      credits: 12,
    },
    {
      task_id: 'mock-usage-acme-journey-001',
      kind: 'journey' as const,
      label: 'Invite a teammate',
      date: daysAgoDate(6),
      credits: 8,
    },
  ]
  const northwindTasks = [
    {
      task_id: 'mock-usage-nw-exp-001',
      kind: 'exploration' as const,
      label: 'Explore the storefront search',
      date: daysAgoDate(2),
      credits: 30,
    },
    {
      task_id: 'mock-usage-nw-followup-001',
      kind: 'task' as const,
      label: 'Review the product gallery',
      date: daysAgoDate(11),
      credits: 18,
    },
  ]

  const total = (tasks: { credits: number }[]) => tasks.reduce((sum, t) => sum + t.credits, 0)
  const acmeTotal = total(acmeTasks)
  const northwindTotal = total(northwindTasks)

  // One ledger per team, so switching teams changes the balance, the spend, AND the project list —
  // which is the only way to tell in mock mode that the team scope is really being applied.
  const ledgers: Record<
    string,
    {
      grant: number
      used: number
      planKey: string | null
      owner: { name: string; email: string }
      projects: UsageData['projects']
    }
  > = {
    [MOCK_TEAM_IDS.own]: {
      grant: freeGrant,
      used: ownTotalUsed,
      planKey: MOCK_TEAM_PLAN_KEYS[MOCK_TEAM_IDS.own]!,
      owner: { name: MOCK_AUTH_USER.name, email: MOCK_AUTH_USER.email },
      projects: ownProjects,
    },
    [MOCK_TEAM_IDS.member]: {
      grant: 500,
      used: acmeTotal,
      // The team plan (shown as "Pro"), so a member can see what funds them.
      planKey: MOCK_TEAM_PLAN_KEYS[MOCK_TEAM_IDS.member]!,
      owner: { name: 'Acme Owner', email: 'owner@acme.com' },
      projects: [
        {
          project_id: 'mock-proj-acme-console',
          project_name: 'Acme admin console',
          total_credits: acmeTotal,
          tasks: acmeTasks,
        },
      ],
    },
    [MOCK_TEAM_IDS.admin]: {
      grant: 500,
      used: northwindTotal,
      // "Pro" — the tier that includes teammates, so an admin here can invite as well as bill.
      planKey: MOCK_TEAM_PLAN_KEYS[MOCK_TEAM_IDS.admin]!,
      owner: { name: 'Dana Okafor', email: 'dana@northwind.example' },
      projects: [
        {
          project_id: 'mock-proj-nw-storefront',
          project_name: 'Northwind storefront',
          total_credits: northwindTotal,
          tasks: northwindTasks,
        },
      ],
    },
  }
  const ledgerFor = (id: string | null | undefined) =>
    (id ? ledgers[id] : null) ?? {
      grant: freeGrant,
      used: ownTotalUsed,
      planKey: MOCK_TEAM_PLAN_KEYS[MOCK_TEAM_IDS.own]!,
      owner: { name: MOCK_AUTH_USER.name, email: MOCK_AUTH_USER.email },
      projects: ownProjects,
    }

  // Every team the mock user belongs to. The team they were invited to is funded well enough to be the
  // server's pick when no team is named, which is what production does too (pickPrimaryTeam) — so the
  // member-side view is what a fresh mock session opens on.
  const teams = mockFetchTeams().items.map((team) => {
    const ledger = ledgerFor(team.id)
    return {
      id: team.id,
      name: team.name,
      role: team.role,
      is_owner: team.is_owner,
      member_count: team.member_count,
      available_credits: Math.max(0, ledger.grant - ledger.used),
      monthly_credits: ledger.grant,
      plan_key: ledger.planKey,
      can_invite: planIncludesTeamCollaboration(ledger.planKey),
      owner_name: ledger.owner.name,
      owner_email: ledger.owner.email,
    }
  })
  const override = creditOverride()
  if (override) {
    const team = {
      id: scope && scope !== 'own' ? scope : MOCK_TEAM_IDS.own,
      name: override.isOwner ? 'My Team' : 'Acme',
      role: override.isOwner ? 'owner' : 'member',
      is_owner: override.isOwner,
      member_count: override.isOwner ? 1 : 4,
      available_credits: override.remaining,
      monthly_credits: override.grant,
      plan_key: override.planKey,
      can_invite: planIncludesTeamCollaboration(override.planKey),
      owner_name: override.isOwner ? MOCK_AUTH_USER.name : 'Acme Owner',
      owner_email: override.isOwner ? MOCK_AUTH_USER.email : 'owner@acme.com',
    }
    return {
      team_id: team.id,
      team_name: team.name,
      teams: [team],
      available_credits: override.remaining,
      total_used: Math.max(0, override.grant - override.remaining),
      usage_waived: override.waived,
      ...(override.waived ? { usage_code: 'CONF2026', usage_code_ends_at: '2026-11-03T00:00:00.000Z' } : {}),
      projects: ownProjects,
    }
  }

  const requested = scope === 'own'
    ? teams.find((team) => team.is_owner) ?? null
    : scope ? teams.find((team) => team.id === scope) ?? null : null
  const active =
    requested ??
    (scope === 'own' ? null : [...teams].sort((a, b) => b.available_credits - a.available_credits)[0]) ??
    null
  const activeLedger = ledgerFor(active?.id)

  return {
    team_id: active?.id ?? null,
    team_name: active?.name ?? null,
    teams,
    available_credits: active
      ? active.available_credits
      : Math.max(0, activeLedger.grant - activeLedger.used),
    total_used: activeLedger.used,
    usage_waived: false,
    projects: activeLedger.projects,
  }
}
